import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { TabPanel, Tabs } from '../ui/Tabs';
import { Badge } from '../ui/Badge';
import { WeekGrid } from './WeekGrid';
import { MixPanel } from './MixPanel';
import { IdeaBank } from './IdeaBank';
import { PostVisual } from './PostVisual';
import { PlatformIcons } from './PlatformIcon';
import { useAdmin } from '../AdminContext';
import { useSocialPosts } from './useSocialPosts';
import { ReadyDesigns } from './ReadyDesigns';
import { MarketingTabs } from '../marketing/MarketingTabs';
import { firstFreeDay as firstFreeDayFrom, postFromDesign } from '../marketing/designPlanning';
import { pillarByKey, statuses } from './socialOptions';
import { btnPrimary, rowLink } from '../ui/styles';
import { addDays, fromIso, iso, relativeDay, shortDate, startOfWeek, today, weekDays, weekNumber } from '../../core/format/dates';
import type { SocialIdea, SocialPost } from '../../core/types/social';
type View = 'week' | 'lijst';

/** In de lijst: eerst wat klaar staat, dan wat nog werk is, geplaatst onderaan. */
const LIST_ORDER: SocialPost['status'][] = ['klaar', 'concept', 'idee', 'geplaatst'];
/** Zoveel geplaatste posts tonen we in de lijst; de rest staat in de weekweergave. */
const POSTED_IN_LIST = 10;

/** De social planner: wat komt er deze week online, en is het klaar? */
export function Social() {
  const {
    marketing,
    setMarketing
  } = useAdmin();
  const {
    posts,
    createPost
  } = useSocialPosts();
  const [params, setParams] = useSearchParams();
  const view: View = params.get('weergave') === 'lijst' ? 'lijst' : 'week';
  const weekParam = params.get('week');
  const monday = startOfWeek(weekParam ? fromIso(weekParam) : new Date());
  const mondayIso = iso(monday);
  const days = weekDays(monday);
  const isThisWeek = mondayIso === iso(startOfWeek(new Date()));
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);else next.set(key, value);
    setParams(next, {
      replace: true
    });
  };
  const goWeek = (d: Date | null) => setParam('week', d ? iso(startOfWeek(d)) : null);
  const designById = (id?: string) => id ? marketing.designs.find(d => d.id === id) : undefined;
  const inRange = (p: SocialPost, from: Date, length: number) => p.date >= iso(from) && p.date <= iso(addDays(from, length - 1));
  const weekPosts = posts.filter(p => inRange(p, monday, 7));
  const aroundPosts = posts.filter(p => inRange(p, addDays(monday, -14), 35));
  const undated = posts.filter(p => !p.date);

  /** Eerste vrije dag vanaf vandaag in de getoonde week, anders vandaag. */
  const firstFreeDay = () => {
    const now = today();
    const free = days.map(iso).find(day => day >= now && !posts.some(p => p.date === day));
    return free ?? (isThisWeek ? now : mondayIso);
  };
  const planIdea = (idea: SocialIdea) => {
    setMarketing(m => ({
      ...m,
      ideas: m.ideas.filter(i => i.id !== idea.id)
    }));
    createPost({
      title: idea.text,
      pillar: idea.pillar,
      status: 'idee',
      date: firstFreeDay()
    });
  };
  const grouped = useMemo(() => LIST_ORDER.flatMap(s => {
    const status = statuses.find(x => x.key === s);
    if (!status) return [];
    const items = posts.filter(p => p.status === s).sort((a, b) => s === 'geplaatst' ? b.date.localeCompare(a.date) : (a.date || '9').localeCompare(b.date || '9'));
    return [{
      status,
      items: s === 'geplaatst' ? items.slice(0, POSTED_IN_LIST) : items
    }];
  }), [posts]);
  return <div data-id="element-4452" data-mp="27mnrkolbq4me">
      <PageHeader title="Social planner" description="Plan je berichten per week, bereid tekst en beeld voor, en vink af wat online staat." actions={<button type="button" onClick={() => createPost({
      date: firstFreeDay()
    })} className={btnPrimary} data-id="element-4454" data-mp="w3l6x2acpkkh">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-4455" data-mp="1cen5wlcc7ecj" />
            Nieuwe post
          </button>} data-id="element-4453" data-mp="r6uow9ezkewf" />
      <MarketingTabs data-id="element-4456" data-mp="12mhi7nt4bj35" />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-4457" data-mp="12o2b78zsatyw">
        {view === 'week' ? <div className="flex items-center gap-1" data-id="element-4458" data-mp="xpr1qzdeilh7">
            <button type="button" onClick={() => goWeek(addDays(monday, -7))} aria-label="Vorige week" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors duration-150 ease-soft hover:bg-white" data-id="element-4459" data-mp="1vc7vjumxegy7">
              <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-4460" data-mp="1gddxkcy1a3s9" />
            </button>
            <p className="min-w-0 px-1 text-center" aria-live="polite" data-id="element-4461" data-mp="9yllwk7gnt6w">
              <span className="block font-display text-[18px] leading-tight text-ink" data-id="element-4462" data-mp="4zihn1zb1qzz">Week {weekNumber(monday)}</span>
              <span className="block text-[12px] text-muted" data-id="element-4463" data-mp="2gn42rs50vw85">
                {shortDate(monday)} – {shortDate(addDays(monday, 6))}
              </span>
            </p>
            <button type="button" onClick={() => goWeek(addDays(monday, 7))} aria-label="Volgende week" className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors duration-150 ease-soft hover:bg-white" data-id="element-4464" data-mp="2ljcib6n4trfu">
              <ChevronRightIcon className="h-4 w-4" strokeWidth={1.75} data-id="element-4465" data-mp="34bwufcem3hwy" />
            </button>
            {!isThisWeek && <button type="button" onClick={() => goWeek(null)} className="ml-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-[13px] text-ink transition-colors duration-150 ease-soft hover:border-ink/40" data-id="element-4466" data-mp="39cb2uvk4ktvi">
                Deze week
              </button>}
          </div> : <p className="text-[14px] text-muted" data-id="element-4467" data-mp="1bvdq0u5k88zt">Alle posts, op volgorde van wat eerst moet.</p>}
        <Tabs id="social-weergave" label="Weergave" variant="pill" value={view} onChange={v => setParam('weergave', v === 'week' ? null : v)} tabs={[{
        key: 'week',
        label: 'Week'
      }, {
        key: 'lijst',
        label: 'Lijst'
      }]} data-id="element-4468" data-mp="17uk3a43ut08i" />
      </div>

      <TabPanel id="social-weergave" value={view} data-id="element-4469" data-mp="1ut6rrt4g6cvm">
      {view === 'week' ? <>
          <WeekGrid days={days} posts={posts} designById={designById} onPlan={date => createPost({
          date
        })} data-id="element-4470" data-mp="1qs4sxdeg02u6" />
          {undated.length > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-4471" data-mp="1zl839ctsjzkl">
              {undated.length} {undated.length === 1 ? 'post heeft' : 'posts hebben'} nog geen datum.{' '}
              <button type="button" onClick={() => setParam('weergave', 'lijst')} className="text-clay underline-offset-4 hover:underline" data-id="element-4472" data-mp="2ej0ehssb21sz">
                Bekijk in de lijst
              </button>
            </p>}
        </> : <div className="space-y-6" data-id="element-4473" data-mp="2x6j3gsfne1ud">
          {posts.length === 0 && <EmptyState icon={CalendarDaysIcon} title="Nog geen posts" text="Begin met een idee uit je ideeënbank hieronder, of plan direct een nieuwe post." action={<button type="button" onClick={() => createPost({
          date: firstFreeDay()
        })} className={btnPrimary} data-id="element-4475" data-mp="29hglfjh3q6zc">
                  Nieuwe post
                </button>} data-id="element-4474" data-mp="2x4op5wyvt7z9" />}
          {grouped.map(({
          status,
          items
        }) => items.length === 0 ? null : <Panel key={status.key} flush title={status.label} actions={<span className="text-[13px] tabular-nums text-muted" data-id="element-4477" data-mp="1qjds4shitnvq">{items.length}</span>} data-id="element-4476" data-mp="25fd7mo6ymqaw">
                <ul className="divide-y divide-line" data-id="element-4478" data-mp="1vge1bc9o36r2">
                  {items.map(p => <li key={p.id} data-id="element-4479" data-mp="2ayfbx6btl2my">
                      <Link to={`/be/social/${p.id}`} className={rowLink} data-id="element-4480" data-mp="2axcjuv2rgu5g">
                        <PostVisual post={p} design={designById(p.designId)} className="h-14 w-11 shrink-0 rounded-sm" data-id="element-4481" data-mp="26yd74oasft3g" />
                        <span className="min-w-0 flex-1" data-id="element-4482" data-mp="220v8pruecw9p">
                          <span className="block truncate text-[14px] text-ink" data-id="element-4483" data-mp="1wymc8libfc2u">{p.title || 'Naamloze post'}</span>
                          <span className="flex items-center gap-2 text-[13px] text-muted" data-id="element-4484" data-mp="1s1e0p3roa6ak">
                            <span className="capitalize" data-id="element-4485" data-mp="1n92sldms3e6x">{relativeDay(p.date)}</span>
                            {p.date && <span className="tabular-nums" data-id="element-4486" data-mp="1j9c2lc52t23g">· {p.time}</span>}
                            <PlatformIcons platforms={p.platforms} data-id="element-4487" data-mp="1e9pqx74wbtwr" />
                          </span>
                        </span>
                        <span className="hidden sm:block" data-id="element-4488" data-mp="198s3af14a7nv">
                          <Badge tone="neutral" data-id="element-4489" data-mp="14ai3ts8d0kwg">{pillarByKey(p.pillar).label}</Badge>
                        </span>
                      </Link>
                    </li>)}
                </ul>
              </Panel>)}
        </div>}
      </TabPanel>

      <ReadyDesigns designs={marketing.designs} posts={posts} onPlan={d => createPost({
      ...postFromDesign(d),
      date: firstFreeDayFrom(posts, isThisWeek ? today() : mondayIso)
    })} data-id="element-4490" data-mp="145iu1he1njp5" />

      <div className="mt-8 grid gap-6 lg:grid-cols-5" data-id="element-4491" data-mp="bh3qbr0d4nt4">
        <div className="lg:col-span-2" data-id="element-4492" data-mp="behal43bj1zt">
          <MixPanel week={weekPosts} around={aroundPosts} data-id="element-4493" data-mp="gf5c4jp6b18a" />
        </div>
        <div className="lg:col-span-3" data-id="element-4494" data-mp="gdc5v5ih2j0t">
          <IdeaBank ideas={marketing.ideas} onChange={ideas => setMarketing(m => ({
          ...m,
          ideas
        }))} onPlan={planIdea} data-id="element-4495" data-mp="ldqcqk8c7mx9" />
        </div>
      </div>

      <p className="mt-8 text-[12px] text-muted" data-id="element-4496" data-mp="q7b25nvv78ku">
        Nog niet gekoppeld aan Instagram of Facebook: je kopieert de tekst, downloadt het beeld, tagt de producten in Instagram en
        zet de status op Geplaatst. Automatisch publiceren, met de producttags, komt met de backend.
      </p>
    </div>;
}